/* eslint-disable react-hooks/exhaustive-deps */
import useFetch from '@hooks/useFetch';
import { processCourse } from '@api/processors/course';
import batchGroup from '@models/BatchGroup';
import { BATCH_GROUPS, DPPS, TOPICS } from '@cont/EducatorProfile/constants';
import { useSelector } from 'react-redux';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline
} from '@selectors/subscription';

import { useSelectedGoalUID } from '../topology/useSelectedGoal';
import usePracticesByEducator from './usePracticesByEducator';

const isBatch = (entity) => !!entity.batchTemplate;

const processProfileFeedData = (
  username,
  profileFeed,
  { dppResult, isFetchingDpps }
) => {
  const newData = profileFeed || {};

  const getDppData = (feedUID, dpps) => {
    const dppData =
      dpps?.results?.find((dpp) => dpp.extraBlockInfo.uid === feedUID) ?? {};

    return {
      dpps: dppData.data ?? [],
      extraBlockInfo: dppData.extraBlockInfo ?? { uid: feedUID }
    };
  };
  newData.results = (newData?.results || [])?.map((feed) => {
    if (feed.type === BATCH_GROUPS) {
      return {
        ...feed,
        key: username,
        label: feed.extraBlockInfo?.title,
        batchGroups: feed?.data?.map((item) => batchGroup.getEntityData(item))
      };
    }
    if (feed.type === TOPICS) {
      return {
        ...feed,
        key: username,
        label: feed.extraBlockInfo?.title,
        data: feed?.data
      };
    }
    if (feed.type === DPPS) {
      const { dpps, extraBlockInfo } = getDppData(
        feed?.extraBlockInfo?.uid,
        dppResult
      );
      return {
        ...feed,
        key: username,
        label: 'Practices',
        dpps,
        extraBlockInfo,
        isFetchingDpps
      };
    }
    return {
      ...feed,
      key: username,
      label: feed.extraBlockInfo?.title,
      data: feed?.data?.map((item) =>
        processCourse({ ...item, ...(item.programme || {}) })
      ),
      courses: (feed.data?.filter?.((course) => !isBatch(course)) || [])?.map?.(
        (course) => processCourse({ ...course, ...(course.programme || {}) })
      ),
      batches: feed.data?.filter?.(isBatch) || []
    };
  });
  return newData;
};

const useProfileFeed = (
  username,
  options = { selector: null, fallbackData: undefined }
) => {
  const selectedGoalUID = useSelectedGoalUID();
  const isPlusSubscription = useSelector(
    getIsSubscribedToPlus(selectedGoalUID)
  );
  const isOfflineSubscription = useSelector(
    getIsSubscribedToOffline(selectedGoalUID)
  );
  const key = `v1/search_v3/educators/${username}/plus/extended/v2/${
    (isPlusSubscription || isOfflineSubscription) && selectedGoalUID
      ? `?goal_uid=${selectedGoalUID}`
      : ''
  }`;
  const { dppResult, isFetchingDpps } = usePracticesByEducator({ username });

  const {
    data: profileFeed,
    isValidating,
    ...rest
  } = useFetch(
    username ? key : null,
    {},
    { fallbackData: options.fallbackData }
  );

  if (options.selector && typeof options.selector === 'function')
    return options.selector(profileFeed || {});

  return {
    key,
    profileFeed: processProfileFeedData(username, Object.seal(profileFeed), {
      dppResult,
      isFetchingDpps
    }),
    isValidating,
    ...rest
  };
};

export default useProfileFeed;
